import { useEffect, useState } from 'react';
import { useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { PinnedSheet, SetRow } from './SetSheet';
import { CatalogueTopSheet } from './Drills';
import { putBrief } from './Brief';

/**
 * «Первые шаги» for a new coach: Расписание, Цены, Горячие часы, Ссылка
 * для учеников. Each ticks by itself and opens the screen where it is
 * done. The Calendar card is for coaches who signed up themselves, until
 * all four are done; the row in Settings stays, and sits at the top while
 * steps are left.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setFirstStepsToken = (t: string) => { authToken = t; };
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), authorization: `Bearer ${authToken}` } });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.json();
}

interface Steps { steps: { schedule: boolean; prices: boolean; hot: boolean; link: boolean; catalogue: boolean }; optional?: { brief: boolean }; shares: boolean; signup: boolean; done: boolean; link: string | null }

/** Where each step is done: a tab, and a section on it to scroll to. */
const go = (tab: string, anchor?: string, day?: string) => window.dispatchEvent(new CustomEvent('coach:go', { detail: { tab, anchor, day } }));

export function useSteps() {
  const [s, setS] = useState<Steps | null>(null);
  const load = () => api<Steps>('/api/coach/first-steps').then(setS).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  return [s, load] as const;
}

export function FirstStepsSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [s, load] = useSteps();
  const [copied, setCopied] = useState(false);
  const [catalogue, setCatalogue] = useState(false);
  async function copy() {
    if (!s?.link) return;
    try { await navigator.clipboard.writeText(s.link); } catch { /* shown below to copy by hand */ }
    setCopied(true);
    haptic.success();
    await api('/api/coach/first-steps/link-shared', { method: 'POST', body: '{}' }).catch(() => {});
    load();
  }
  async function skipHot() {
    haptic.tap();
    await api('/api/coach/first-steps/no-hot', { method: 'POST', body: '{}' }).catch(() => {});
    load();
  }
  const row = (done: boolean, title: string, hint: string, action: React.ReactNode) => (
    <div className={`fs-step${done ? ' fs-step--done' : ''}`}>
      <span className="fs-step__tick" aria-hidden="true">{done ? '✓' : ''}</span>
      <span className="fs-step__main"><b>{title}</b><small>{hint}</small>{action}</span>
    </div>
  );
  return (
    <PinnedSheet title={t('fs.title')} onClose={onClose}
      footer={<button className="paybar__opt" onClick={onClose}>{t('bill.close')}</button>}>
      {!s ? <div className="skeleton" /> : (
        <div className="fs">
          {row(s.steps.schedule, t('fs.schedule'), t('fs.scheduleHint'),
            <button className="sess__link" onClick={() => { onClose(); go('settings', 'set-hours'); }}>{t('fs.open')}</button>)}
          {row(s.steps.prices, t('fs.prices'), t('fs.pricesHint'),
            <button className="sess__link" onClick={() => { onClose(); go('settings', 'set-prices'); }}>{t('fs.open')}</button>)}
          {row(s.steps.hot, t('fs.hot'), t('fs.hotHint'),
            <span className="fs-actions">
              <button className="sess__link" onClick={async () => {
                // The Calendar on the first day with open times (today if any are left).
                const r = await api<{ day: string | null }>('/api/coach/next-open-day').catch(() => ({ day: null }));
                onClose(); go('home', 'open-times', r.day ?? undefined);
              }}>{t('fs.open')}</button>
              {!s.steps.hot && (
                <button className="sess__link fs-skip" onClick={skipHot}>{t('fs.noHot')}</button>
              )}
            </span>)}
          {row(s.steps.link, t('fs.link'), t('fs.linkHint'),
            <span className="fs-link">
              {s.link && <code className="fs-link__url">{s.link}</code>}
              <button className="savebtn fs-link__copy" disabled={!s.link} onClick={copy}>{copied ? t('fs.copied') : t('fs.copy')}</button>
            </span>)}
          {row(s.steps.catalogue, t('fs.catalogue'), t('fs.catalogueHint'),
            <button className="sess__link" onClick={() => { haptic.tap(); setCatalogue(true); }}>{t('fs.open')}</button>)}
          {/* Optional: not counted in «Готово N из 5». */}
          {row(!!s.optional?.brief, t('fs.brief'), t('fs.briefHint'),
            s.optional?.brief ? null : (
              <button className="sess__link" onClick={async () => {
                haptic.tap();
                await putBrief({ on: true, mode: 'evening', time: '21:00' }).catch(() => {});
                load();
              }}>{t('fs.briefOn')}</button>
            ))}
        </div>
      )}
      {catalogue && <CatalogueTopSheet onClose={() => { setCatalogue(false); load(); }} />}
    </PinnedSheet>
  );
}

/** Top of the Calendar for a coach who signed up themselves, while steps are left. */
export function FirstStepsCard({ openAtStart = false }: { openAtStart?: boolean }) {
  const t = useT();
  const [s, load] = useSteps();
  const [open, setOpen] = useState(openAtStart);
  // Sign-up lands here with ?steps=1: open once, then drop it from the
  // address, or the sheet would open again on every return to the Calendar.
  useEffect(() => {
    if (!openAtStart) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('steps');
    window.history.replaceState(null, '', url.toString());
  }, []);
  if (!s && !open) return null;
  const total = s ? Object.keys(s.steps).length : 5;
  const left = s ? Object.values(s.steps).filter((x) => !x).length : total;
  return (
    <>
      {s && s.signup && !s.done && (
        <button className="fs-card" onClick={() => { haptic.tap(); setOpen(true); }}>
          <b>{t('fs.title')}</b>
          <span>{t('fs.left', { n: total - left })}</span>
        </button>
      )}
      {open && <FirstStepsSheet onClose={() => { setOpen(false); load(); }} />}
    </>
  );
}

/**
 * The same steps from Settings. `top`: the first thing in Settings for a
 * coach who signed up and still has steps left. Otherwise it is a row
 * among the rest.
 */
export function FirstStepsRow({ place }: { place: 'top' | 'setup' }) {
  const t = useT();
  const [s] = useSteps();
  const [open, setOpen] = useState(false);
  const pending = !!s && s.signup && !s.done;
  if (!s || (place === 'top') !== pending) return null;
  const total = Object.keys(s.steps).length;
  const left = total - Object.values(s.steps).filter(Boolean).length;
  const rowEl = (
    <SetRow id="setup-first-steps" icon="🚀" title={t('fs.title')}
      status={pending ? t('fs.left', { n: total - left }) : t('fs.rowStatus')} onOpen={() => setOpen(true)} />
  );
  return (
    <>
      {place === 'top' ? <div className="list"><div className="card setrows">{rowEl}</div></div> : rowEl}
      {open && <FirstStepsSheet onClose={() => setOpen(false)} />}
    </>
  );
}
